import { useState, useEffect } from 'react';
import { supabase } from '../config/supabase';

interface ParametroPerfil {
  id: string;
  tipo_usuario: string;
  max_empresas: number;
  max_eleitores: number;
  max_funcionarios: number;
  descricao: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export const useParametrosPerfil = (tipoUsuario: string) => {
  const [parametros, setParametros] = useState<ParametroPerfil | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const carregarParametros = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('cp_parametros_perfil')
        .select('*')
        .eq('tipo_usuario', tipoUsuario)
        .eq('ativo', true)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      setParametros(data);
    } catch (err) {
      console.error('Erro ao carregar parâmetros do perfil:', err);
      setError('Erro ao carregar parâmetros do perfil');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tipoUsuario) {
      carregarParametros();
    }
  }, [tipoUsuario]);

  // Verificar se pode adicionar mais empresas
  const podeAdicionarEmpresa = (empresasAtuais: number) => {
    if (!parametros) return false;
    if (parametros.max_empresas === -1) return true; // Ilimitado
    return empresasAtuais < parametros.max_empresas;
  };

  // Verificar se pode adicionar mais eleitores
  const podeAdicionarEleitor = (eleitoresAtuais: number) => {
    if (!parametros) return false;
    if (parametros.max_eleitores === -1) return true; // Ilimitado
    return eleitoresAtuais < parametros.max_eleitores;
  };

  // Verificar se pode ter mais funcionários
  const podeTerFuncionarios = (funcionariosAtuais: number) => {
    if (!parametros) return false;
    if (parametros.max_funcionarios === -1) return true; // Ilimitado
    return funcionariosAtuais <= parametros.max_funcionarios;
  };

  // Obter limite de empresas
  const getLimiteEmpresas = () => {
    if (!parametros) return 0;
    return parametros.max_empresas === -1 ? 'Ilimitado' : parametros.max_empresas;
  };

  // Obter limite de eleitores
  const getLimiteEleitores = () => {
    if (!parametros) return 0;
    return parametros.max_eleitores === -1 ? 'Ilimitado' : parametros.max_eleitores;
  };

  // Obter limite de funcionários
  const getLimiteFuncionarios = () => {
    if (!parametros) return 0;
    return parametros.max_funcionarios === -1 ? 'Ilimitado' : parametros.max_funcionarios;
  };

  // Obter empresas restantes
  const getEmpresasRestantes = (empresasAtuais: number) => {
    if (!parametros) return 0;
    if (parametros.max_empresas === -1) return 'Ilimitado';
    return Math.max(0, parametros.max_empresas - empresasAtuais);
  };

  // Obter eleitores restantes
  const getEleitoresRestantes = (eleitoresAtuais: number) => {
    if (!parametros) return 0;
    if (parametros.max_eleitores === -1) return 'Ilimitado';
    return Math.max(0, parametros.max_eleitores - eleitoresAtuais);
  };

  // Obter funcionários restantes
  const getFuncionariosRestantes = (funcionariosAtuais: number) => {
    if (!parametros) return 0;
    if (parametros.max_funcionarios === -1) return 'Ilimitado';
    return Math.max(0, parametros.max_funcionarios - funcionariosAtuais);
  };

  return {
    parametros,
    loading,
    error,
    podeAdicionarEmpresa,
    podeAdicionarEleitor,
    podeTerFuncionarios,
    getLimiteEmpresas,
    getLimiteEleitores,
    getLimiteFuncionarios,
    getEmpresasRestantes,
    getEleitoresRestantes,
    getFuncionariosRestantes,
    recarregar: carregarParametros
  };
};
